import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeaders() {
  const password = sessionStorage.getItem('admin_password');
  if (!password) return {};
  return {
    Authorization: `Basic ${btoa(':' + password)}`
  };
}

export default function CategoriesManager({ onRefresh }) {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      await axios.post(`${API}/categories?name=${encodeURIComponent(newCategory)}`, null, { headers: getAuthHeaders() });
      toast.success('Category created');
      setNewCategory('');
      fetchCategories();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Projects using it will keep the category name.')) return;

    try {
      await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeaders() });
      toast.success('Category deleted');
      fetchCategories();
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  return (
    <div className="space-y-6" data-testid="categories-manager">
      <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Categories</h2>

      {/* Create Form */}
      <Card className="p-6">
        <form onSubmit={handleCreate} className="flex gap-4">
          <div className="flex-1">
            <Label htmlFor="newCategory">New Category</Label>
            <Input
              id="newCategory"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="e.g., Residential, Commercial, Urban"
              data-testid="new-category-input"
            />
          </div>
          <Button type="submit" className="mt-auto btn-hover" style={{ background: '#b8d71b', color: '#1a1a1a' }} data-testid="add-category-btn">
            <Plus size={20} className="mr-2" />
            Add Category
          </Button>
        </form>
      </Card>

      {/* Categories List */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {loading ? (
          [...Array(6)].map((_, i) => <div key={i} className="skeleton h-20" />)
        ) : categories.length === 0 ? (
          <Card className="col-span-full p-12 text-center">
            <p className="text-gray-400">No categories yet. Create your first one!</p>
          </Card>
        ) : (
          categories.map((cat) => (
            <Card key={cat.id} className="p-4 flex items-center justify-between hover:shadow-lg transition-shadow" data-testid={`category-card-${cat.slug}`}>
              <div>
                <p className="font-semibold">{cat.name}</p>
                <p className="text-xs text-gray-500">{cat.slug}</p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => handleDelete(cat.id)}
                data-testid={`delete-category-${cat.slug}`}
              >
                <Trash2 size={16} className="text-red-500" />
              </Button>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}